import {
  Button,
  Create,
  Datagrid,
  Edit,
  List,
  TextField,
  useDataProvider,
  useNotify,
  useRedirect,
  useResourceContext,
} from 'react-admin';
import { CreativesListActions } from './clicks-creatives';
import { ESTDateField } from './custom-ra-fields/est-date-field';
import { FmaCreativeForm } from './forms/fma-creatives';
import LaunchIcon from '@mui/icons-material/Launch';
import { useSearchParams } from 'react-router-dom';
import { useRecordContext } from 'react-admin';
import { useTransformWithCampaignId } from './forms/hooks/useTransformWithCampaignId';
import {
  resolveCreativeParentLineItemShowPath,
  withPlacementsLineItemIdFromNativeForEdit,
} from 'src/client/utility/pio-native-lineitem-fk-reads';

export const FmaCreativesList = () => (
  <List actions={<CreativesListActions />}>
    <Datagrid rowClick="edit">
      <TextField source="id" />
      <ESTDateField source="startsAt" />
      <ESTDateField source="endsAt" />
    </Datagrid>
  </List>
);

interface FmaCreativeCreateInitialValues {
  placementsLineItemId: string;
}

export const FmaCreativeCreate = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const [searchParams] = useSearchParams();
  const placementsLineItemId = searchParams.get('placementsLineItemId') ?? '';
  const transform = useTransformWithCampaignId();

  const resource = useResourceContext();

  const record: FmaCreativeCreateInitialValues = {
    placementsLineItemId,
  };

  // The legacy /line_items show page can no longer load (PIO API shut off), so
  // resolve a legacy numeric id to its native UUID and land on /lineItems.
  // Prefer the submitted selection over the seeding query param — the user may
  // have picked a different line item in the form.
  const onSuccess = async (data?: Record<string, any>, variables?: Record<string, any>) => {
    notify('Saved new FMA Creative');
    const path = await resolveCreativeParentLineItemShowPath(dataProvider, variables?.data, { placementsLineItemId });
    redirect(path ?? 'list', resource);
  };

  return (
    <Create record={record} mutationOptions={{ onSuccess }} redirect="list" transform={transform}>
      <FmaCreativeForm type="Create" />
    </Create>
  );
};

export const FmaCreativeEdit = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const resource = useResourceContext();

  const onSuccess = async (data?: Record<string, any>, variables?: Record<string, any>) => {
    notify('Saved FMA Creative');
    // MD-5145: resolve the parent line item from the edited record (submitted data /
    // previousData), not the mutation response. All rows route to the native
    // /lineItems show page; a legacy-only id is translated via externalPioId.
    const path = await resolveCreativeParentLineItemShowPath(dataProvider, variables?.data, variables?.previousData, data);
    redirect(path ?? 'list', resource);
  };

  return (
    <Edit
      mutationMode="pessimistic"
      mutationOptions={{ onSuccess }}
      redirect="list"
      queryOptions={{ select: withPlacementsLineItemIdFromNativeForEdit }}
    >
      <FmaCreativeForm type="Update" />
    </Edit>
  );
};

/** A Button that when clicked, opens a browser tab to preview the FMA */
export const PreviewFMAButton = () => {
  const record = useRecordContext();

  return record?.id ? (
    <a href={`${process.env.NEXT_PUBLIC_MDLINX_BASE_URL}/?preview_fma=${record?.id}`} target="_blank" rel="noreferrer">
      <Button label="Preview" alignIcon="right" onClick={e => e.stopPropagation()}>
        <LaunchIcon />
      </Button>
    </a>
  ) : null;
};
